import React, { useCallback, useEffect, useState } from "react";
import {
    Box,
    Breadcrumbs,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    IconButton,
    Link,
    List,
    ListItem,
    ListItemText,
    Paper,
    TextField,
    Typography,
    useMediaQuery,
} from "@material-ui/core";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { Close } from "@material-ui/icons";
import { Skeleton } from "@material-ui/lab";
import { useSnackbar } from "notistack";
import { fileService } from "@services";
import { useAsync } from "@hooks";
import { resolveDirectoryChain } from "@hooks/directoryCache";
import { Directory } from "@interfaces/Directory";
import { File as AppFile } from "@interfaces/File";
import SelectableDirectoryTree from "./SelectableDirectoryTree";
import FilePickerFileGrid from "./FilePickerFileGrid";
import { DropzoneField } from "@components/Controls/Inputs";

const useStyles = makeStyles(() => ({
    content: {
        height: "70vh",
    },
    column: {
        height: "100%",
        display: "flex",
        flexDirection: "column",
    },
    scrollArea: {
        flex: 1,
        minHeight: 0,
        overflowY: "auto",
    },
}));

type Props = {
    open: boolean;
    value?: number | null;
    onSelect?(fileId: number): void;
    onClose(): void;
    /** режим множественного выбора — "Добавить несколько файлов из хранилища":
     * выбор не заменяет одно значение, а копится в список, подтверждение
     * зовёт onSelectMultiple вместо onSelect. Загрузка с компьютера в этом
     * режиме скрыта — для неё уже есть экран "Файловое хранилище", дублировать
     * его тут не нужно (менеджер грузит файлы туда заранее). */
    multiple?: boolean;
    onSelectMultiple?(fileIds: number[]): void;
    /** id файлов, уже занятых в этом атрибуте — нельзя выбрать повторно */
    excludeFileIds?: number[];
    /** если нет value (нечего заякорить) — при открытии ищем папку по этому
     * названию и сразу переходим к первому совпадению, вместо вершины дерева */
    initialSearchTerm?: string;
};

/**
 * Собственный, независимый от глобального ModalManager диалог: поле
 * "Файл"/"Изображение" атрибута сущности открывается ИЗНУТРИ уже открытой
 * модалки редактирования сущности (тот же глобальный Dialog), и попытка
 * переиспользовать для него тот же MODAL_TYPE подменяла собой родительскую
 * модалку (там всего один активный слот) — размонтировала форму сущности
 * вместе с её unmount-эффектами (снятие блокировки редактирования и т.п.).
 */
const FilePickerDialog = ({
    open,
    value = null,
    onSelect,
    onClose,
    multiple = false,
    onSelectMultiple,
    excludeFileIds,
    initialSearchTerm,
}: Props) => {
    const classes = useStyles();
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
    const { enqueueSnackbar } = useSnackbar();

    const [selectedFileId, setSelectedFileId] = useState<number | null>(value);
    const [selectedFileIds, setSelectedFileIds] = useState<number[]>([]);
    const [currentDirectoryId, setCurrentDirectoryId] = useState<number | null>(null);
    const [uploadRefreshKey, setUploadRefreshKey] = useState(0);
    /** Готовность дерева к первому монтированию — выставляется один раз за
     * сессию открытия диалога и НЕ должна зависеть от pathLoadingStatus
     * дальнейшей навигации (иначе дерево перемонтируется при каждом клике
     * по папке — сброс скролла и схлопывание развёрнутых узлов). */
    const [initialReady, setInitialReady] = useState(false);

    /** Дерево всегда показывает ПОЛНОЕ хранилище (не сужается до ветки —
     * первая попытка так делать только мешала: пользователю нужно видеть
     * и свободно скроллить всё дерево, просто сразу открытым на нужном
     * месте). autoOpenIds — какие узлы развернуть, scrollTargetId — какой
     * узел подвести к верхней строке видимой области. Оба актуальны только
     * в момент монтирования дерева (TreeView неконтролируемый) — дальнейшие
     * изменения (например прыжок по поиску) применяются через treeVersion,
     * пересоздающую дерево заново с новыми значениями. */
    const [autoOpenIds, setAutoOpenIds] = useState<Set<number>>(new Set());
    const [scrollTargetId, setScrollTargetId] = useState<number | null>(null);
    const [treeVersion, setTreeVersion] = useState(0);
    const [initialScrollDecided, setInitialScrollDecided] = useState(false);

    const [searchTerm, setSearchTerm] = useState("");
    const [searchResults, setSearchResults] = useState<Directory[]>([]);
    const [fileSearchTerm, setFileSearchTerm] = useState("");
    const [debouncedFileSearch, setDebouncedFileSearch] = useState("");

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedFileSearch(fileSearchTerm), 400);
        return () => clearTimeout(timer);
    }, [fileSearchTerm]);

    // при смене папки поиск файла сбрасывается — он относится к одной папке
    useEffect(() => {
        setFileSearchTerm("");
        setDebouncedFileSearch("");
    }, [currentDirectoryId]);

    /** Breadcrumb больше НЕ пересчитывается хуком на каждый currentDirectoryId —
     * устанавливается напрямую из уже известной цепочки (клик по дереву несёт
     * её с собой, см. SelectableDirectoryTreeItem) или из клика по самому
     * breadcrumb'у (там просто обрезаем уже имеющийся path). Сетевой запрос
     * нужен только там, где цепочка ЕЩЁ не известна — при открытии диалога и
     * при прыжке по результату поиска. Раньше сетевой round-trip шёл на КАЖДЫЙ
     * клик по дереву (пусть и дешёвый после кэша) — это и давало задержку,
     * которой нет в экране "Файловое хранилище" (там выбор вообще не ходит в
     * сеть). */
    const [path, setPath] = useState<Directory[]>([]);

    useEffect(() => {
        if (!open) return;

        setSelectedFileId(value);
        setSelectedFileIds([]);
        setCurrentDirectoryId(null);
        setUploadRefreshKey(0);
        setInitialReady(false);
        setAutoOpenIds(new Set());
        setScrollTargetId(null);
        setInitialScrollDecided(false);
        setSearchTerm("");
        setSearchResults([]);
        setPath([]);
        // при каждом открытии диалога — заново, с текущим value
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const { data: rootDirectory, loadingStatus: rootLoadingStatus } = useAsync<Directory>(
        fileService.getDirectoriesTree,
        { setLoadingStatusOnUpdate: false, canLoad: open }
    );

    useEffect(() => {
        if (!open || !value) return;

        fileService.getOneFile(value).then((file) => {
            if (file?.dir_id) setCurrentDirectoryId(file.dir_id);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    useEffect(() => {
        if (!open || value || currentDirectoryId !== null) return;

        if (!initialSearchTerm) {
            if (rootDirectory) setCurrentDirectoryId(rootDirectory.id);
            return;
        }

        // Якоря (value) нет — это первое фото у SKU. Вместо вершины дерева
        // ищем папку по названию модели (initialSearchTerm — "Артикул модели"
        // из карточки) и сразу переходим к ней.
        let cancelled = false;

        fileService.getDirectories({ q: initialSearchTerm, page_size: 1 }).then(async ({ result }) => {
            if (cancelled) return;

            const match = result.items[0];

            if (!match) {
                // не нашли папку с таким названием — открываемся с вершины, как раньше
                if (rootDirectory) setCurrentDirectoryId(rootDirectory.id);
                return;
            }

            // найденная папка — само место назначения (не файл внутри неё, как
            // в резолве по value ниже) — скроллим к НЕЙ самой, а не к родителю
            const chain = await resolveDirectoryChain(match.id);
            if (cancelled || !chain.length) return;

            setPath(chain);
            setAutoOpenIds(new Set(chain.slice(0, -1).map((dir) => dir.id)));
            setScrollTargetId(match.id);
            setInitialScrollDecided(true);
            setCurrentDirectoryId(match.id);
        });

        return () => {
            cancelled = true;
        };
    }, [open, value, initialSearchTerm, rootDirectory, currentDirectoryId]);

    useEffect(() => {
        // решаем один раз за сессию открытия (guard — initialScrollDecided);
        // единственное место во всём диалоге, где breadcrumb реально строится
        // сетевым обходом предков — дальнейшая навигация (клик по дереву или
        // по самому breadcrumb) переиспользует уже известную цепочку локально.
        // target: path[length-1] — папка с самим файлом; path[length-2] (если
        // есть) — её родитель, то есть модель (не бренд — в бренде моделей
        // бывает по несколько сот, это слишком широко).
        if (initialScrollDecided) return;
        if (!currentDirectoryId) return;

        let cancelled = false;

        resolveDirectoryChain(currentDirectoryId).then((chain) => {
            if (cancelled || !chain.length) return;

            const target = chain.length > 1 ? chain[chain.length - 2] : chain[chain.length - 1];

            setPath(chain);
            setAutoOpenIds(new Set(chain.slice(0, -1).map((dir) => dir.id)));
            setScrollTargetId(target.id);
            setInitialScrollDecided(true);
        });

        return () => {
            cancelled = true;
        };
    }, [initialScrollDecided, currentDirectoryId]);

    useEffect(() => {
        const term = searchTerm.trim();

        if (!term) {
            setSearchResults([]);
            return;
        }

        let cancelled = false;
        const timer = setTimeout(async () => {
            const { result } = await fileService.getDirectories({ q: term, page_size: 20 });
            if (!cancelled) setSearchResults(result.items);
        }, 400);

        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [searchTerm]);

    useEffect(() => {
        if (initialReady) return;
        if (rootLoadingStatus !== "loaded" || !rootDirectory) return;
        // для открытия с уже выбранным значением (или с поиском папки по
        // названию модели) ждём ещё и решения, куда проскроллить и что
        // развернуть — чтобы дерево сразу смонтировалось в нужном месте
        if ((value || initialSearchTerm) && !initialScrollDecided) return;

        setInitialReady(true);
    }, [initialReady, rootLoadingStatus, rootDirectory, value, initialSearchTerm, initialScrollDecided]);

    // useCallback — не косметика: SelectableDirectoryTreeItem мемоизирован
    // именно чтобы не пере-рендерить тысячи узлов дерева на каждый клик, а
    // не-стабильная ссылка на onSelect свела бы эту мемоизацию на нет (она
    // передаётся ОДИНАКОВОЙ во все узлы дерева). chain приходит ГОТОВЫМ от
    // дерева (см. SelectableDirectoryTreeItem) — без единого сетевого запроса,
    // дерево и так уже построено из этих же данных.
    const handleSelectDirectory = useCallback((chain: Directory[]) => {
        const target = chain[chain.length - 1];
        setCurrentDirectoryId(target.id);
        setPath(chain);
    }, []);

    // не обёрнуто в useCallback: не передаётся в мемоизированные узлы дерева,
    // читает path напрямую из замыкания — пересоздание на каждый рендер
    // здесь ничего не стоит (это всего пара элементов в breadcrumb, не
    // тысячи узлов дерева). Путь уже известен (это кусок уже имеющегося
    // path) — сети не нужно, в отличие от handleJumpToDirectory.
    const handleBreadcrumbClick = (index: number) => {
        const next = path.slice(0, index + 1);
        const target = next[next.length - 1];

        setCurrentDirectoryId(target.id);
        setPath(next);
        setAutoOpenIds(new Set(next.slice(0, -1).map((dir) => dir.id)));
        setScrollTargetId(target.id);
        setTreeVersion((prev) => prev + 1);
    };

    const handleCollapseAll = () => {
        setAutoOpenIds(new Set());
        setScrollTargetId(null);
        setTreeVersion((prev) => prev + 1);
    };

    const handleJumpToDirectory = async (directory: Directory) => {
        // в отличие от клика по уже построенному дереву — найденная поиском
        // папка не обязательно есть среди уже смонтированных узлов, путь к
        // ней неизвестен заранее, поэтому здесь сетевой обход предков всё
        // же нужен (кэшируется той же resolveDirectoryChain)
        const chain = await resolveDirectoryChain(directory.id);
        if (!chain.length) return;

        setPath(chain);
        setAutoOpenIds(new Set(chain.slice(0, -1).map((dir) => dir.id)));
        setScrollTargetId(directory.id);
        setTreeVersion((prev) => prev + 1);
        setCurrentDirectoryId(directory.id);
        setSearchTerm("");
        setSearchResults([]);
    };

    const handleSelectFile = (file: AppFile) => {
        if (multiple) {
            setSelectedFileIds((prev) =>
                prev.includes(file.id) ? prev.filter((id) => id !== file.id) : [...prev, file.id]
            );
            return;
        }

        setSelectedFileId(file.id);
    };

    const isFileSelected = (fileId: number) =>
        multiple ? selectedFileIds.includes(fileId) : fileId === selectedFileId;

    const handleUpload = async (files: File[]) => {
        if (!currentDirectoryId) return;

        try {
            const fileId = await fileService.uploadFile(files[0], {
                directoryId: currentDirectoryId,
            });

            setSelectedFileId(fileId);
            setUploadRefreshKey((prev) => prev + 1);
            enqueueSnackbar("Файл успешно загружен", { variant: "success" });
        } catch (err) {
            const message = err.response?.data?.error_tooltip;
            enqueueSnackbar(message || "Произошла ошибка при загрузке файла", {
                variant: "error",
            });
        }
    };

    const handleConfirm = () => {
        if (multiple) {
            if (!selectedFileIds.length) return;
            onSelectMultiple?.(selectedFileIds);
            onClose();
            return;
        }

        if (!selectedFileId) return;
        onSelect?.(selectedFileId);
        onClose();
    };

    return (
        <Dialog open={open} onClose={onClose} fullScreen={fullScreen} fullWidth maxWidth="lg">
            <DialogTitle disableTypography>
                <Typography variant="h6">
                    {multiple ? "Добавить файлы из хранилища" : "Выбрать файл"}
                </Typography>
                <IconButton
                    style={{ position: "absolute", right: 8, top: 8 }}
                    onClick={onClose}
                >
                    <Close />
                </IconButton>
            </DialogTitle>
            <DialogContent className={classes.content}>
                <Grid container spacing={2} style={{ height: "100%" }}>
                    <Grid item xs={12} md={5} className={classes.column}>
                        <Grid container justifyContent="space-between" alignItems="center">
                            <Typography variant="subtitle2" gutterBottom>
                                Папки
                            </Typography>
                            <Link component="button" variant="body2" onClick={handleCollapseAll}>
                                Свернуть всё
                            </Link>
                        </Grid>

                        <TextField
                            size="small"
                            fullWidth
                            variant="outlined"
                            placeholder="Поиск папки по названию"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={{ marginBottom: 8 }}
                        />

                        {searchResults.length > 0 && (
                            <Paper variant="outlined" style={{ maxHeight: 200, overflowY: "auto", marginBottom: 8 }}>
                                <List dense disablePadding>
                                    {searchResults.map((dir) => (
                                        <ListItem
                                            button
                                            key={dir.id}
                                            onClick={() => handleJumpToDirectory(dir)}
                                        >
                                            <ListItemText primary={dir.title} />
                                        </ListItem>
                                    ))}
                                </List>
                            </Paper>
                        )}

                        {!initialReady && <Skeleton variant="rect" height={300} />}

                        {initialReady && rootDirectory && (
                            <div className={classes.scrollArea}>
                                <SelectableDirectoryTree
                                    key={treeVersion}
                                    root={rootDirectory}
                                    selectedId={currentDirectoryId}
                                    autoOpenIds={autoOpenIds}
                                    scrollTargetId={scrollTargetId}
                                    onSelect={handleSelectDirectory}
                                />
                            </div>
                        )}
                    </Grid>

                    <Grid item xs={12} md={7} className={classes.column}>
                        <Breadcrumbs>
                            {path.map((dir, index) => {
                                const isLast = index === path.length - 1;

                                return isLast ? (
                                    <Typography key={dir.id} color="textPrimary">
                                        {dir.title}
                                    </Typography>
                                ) : (
                                    <Link
                                        key={dir.id}
                                        component="button"
                                        variant="body2"
                                        onClick={() => handleBreadcrumbClick(index)}
                                    >
                                        {dir.title}
                                    </Link>
                                );
                            })}
                        </Breadcrumbs>

                        <TextField
                            size="small"
                            fullWidth
                            variant="outlined"
                            placeholder="Поиск файла в этой папке"
                            value={fileSearchTerm}
                            onChange={(e) => setFileSearchTerm(e.target.value)}
                            style={{ marginTop: 8 }}
                        />

                        <Box my={2} className={classes.scrollArea}>
                            <FilePickerFileGrid
                                directoryId={currentDirectoryId}
                                searchTerm={debouncedFileSearch}
                                isSelected={isFileSelected}
                                onSelectFile={handleSelectFile}
                                refreshKey={uploadRefreshKey}
                                excludeFileIds={excludeFileIds}
                            />
                        </Box>

                        {!multiple && (
                            <>
                                <Divider />

                                <Box mt={2}>
                                    <Typography variant="subtitle2" gutterBottom>
                                        Загрузить с компьютера
                                    </Typography>
                                    {currentDirectoryId ? (
                                        <DropzoneField onSubmit={handleUpload} maxFiles={1} preview />
                                    ) : (
                                        <Typography color="textSecondary">
                                            Сначала выберите папку
                                        </Typography>
                                    )}
                                </Box>
                            </>
                        )}
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Отмена</Button>
                <Button
                    color="primary"
                    variant="contained"
                    disabled={multiple ? !selectedFileIds.length : !selectedFileId}
                    onClick={handleConfirm}
                >
                    {multiple ? `Добавить (${selectedFileIds.length})` : "Выбрать"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default FilePickerDialog;
